import { inject, Injectable } from '@angular/core';
import { DbService } from '../db/db.service';
import { Project } from '../../models/project';
import { User } from '../../models/user';
import { CategoryEnrollment } from '../../models/categoryEnrollment';
import { CourseEnrollment } from '../../models/courseEnrollment';
import { ChapterEnrollment, QuizAttemptHistory } from '../../models/chapterEnrollment';
import { Note } from '../../models/note';
import { DocumentReference } from '@angular/fire/compat/firestore';
import { isNullOrUndefined, isValidNumber, isValidString } from '@pineapplelab/util';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { Router } from '@angular/router';
import firebase from "firebase/compat/app";
import { Quiz } from '../../models/quiz';
import { canAccessCourseInOrder } from '../../utils/course-access.utils';
import { computeCompletedPercentage } from '../../utils/course-progress.utils';
import { PlaybackDiagnosticsService } from '../playback-diagnostics/playback-diagnostics.service';
import { ProgressSyncContext, ProgressSyncService } from '../progress-sync/progress-sync.service';

// Subcollection the server archives each finished attempt into, one document per attempt.
const QUIZ_ATTEMPT_HISTORY_PATH = 'quizAttemptHistory';

// Pause before each retry of a progress write; the final `null` marks the last attempt.
const SAVE_ATTEMPT_DELAYS_MS: (number | null)[] = [500, 1500, null];
const SAVE_ATTEMPT_TIMEOUT_MS = 10_000;
// How long a write waits for the previous write to the same document before running anyway. A
// transaction with no connectivity can take a long time to give up; it must not hold every later
// save hostage.
const WRITE_QUEUE_MAX_WAIT_MS = 30_000;
const WRITE_TIMEOUT_MESSAGE = 'Firestore write timed out';

// Rejections that no amount of retrying will fix: the caller is signed out, or the payload is malformed.
const NON_RETRYABLE_FIRESTORE_CODES = new Set(['permission-denied', 'unauthenticated', 'invalid-argument']);

function isRetryableFirestoreError(error: any): boolean {
	return !NON_RETRYABLE_FIRESTORE_CODES.has(error?.code);
}

/**
 * Flattens a thrown value into something `JSON.stringify` preserves.
 *
 * `Error` keeps `message`, `name` and `code` on the prototype or as non-enumerable properties, so
 * stringifying one yields "{}" — which is how every write timeout reached the log, indistinguishable
 * from a failure with no error at all.
 */
function describeError(error: any): { [key: string]: any } {
	if (isNullOrUndefined(error)) {
		return { reason: 'unknown error' };
	}
	if (typeof error !== 'object') {
		return { reason: String(error) };
	}
	return {
		...error,
		name: error?.name ?? null,
		code: error?.code ?? null,
		message: error?.message ?? String(error),
	};
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
	let timer: ReturnType<typeof setTimeout>;
	const timeout = new Promise<never>((_, reject) => {
		timer = setTimeout(() => reject(new Error(`${WRITE_TIMEOUT_MESSAGE} after ${timeoutMs}ms`)), timeoutMs);
	});
	return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

@Injectable({
	providedIn: 'root'
})
export class CourseEnrollmentService {

	private db = inject(DbService);
	private authService = inject(AuthService);
	private router = inject(Router);
	private diagnostics = inject(PlaybackDiagnosticsService);
	private progressSync = inject(ProgressSyncService);
	// Tail of the write queue per enrollment document. Writes to one document run one at a time: two
	// transactions from this client on the same document only invalidate each other's reads.
	private writeQueues = new Map<string, Promise<unknown>>();

	/**
	 * Attempts finished and archived for a chapter.
	 *
	 * Besides providing the trustworthy floor for the active attempt, the complete entries let the
	 * consumer keep showing the result that was just archived when a grant clears the live score and
	 * answers from the chapter enrollment.
	 */
	async getQuizAttemptHistory(
		userId: string,
		categoryEnrollmentId: string,
		courseEnrollmentId: string,
		chapterEnrollmentId: string,
		projectId: string
	): Promise<QuizAttemptHistory[]> {
		if (!userId || !categoryEnrollmentId || !courseEnrollmentId || !chapterEnrollmentId) {
			return [];
		}

		try {
			const snapshot = await this.db.getProyIdRef(projectId)
				.collection(User.PATH).doc(userId)
				.collection(CategoryEnrollment.PATH).doc(categoryEnrollmentId)
				.collection(CourseEnrollment.PATH).doc(courseEnrollmentId)
				.collection(ChapterEnrollment.PATH).doc(chapterEnrollmentId)
				.collection(QUIZ_ATTEMPT_HISTORY_PATH)
				.get();

			return snapshot.docs
				.map((doc: any) => {
					const data = doc.data() ?? {};
					return {
						attempt: Number(data.attempt ?? doc.id) || 0,
						quizScore: Number(data.quiz_score ?? data.quizScore) || 0,
						totalQuestions: Number(data.total_questions ?? data.totalQuestions) || 0,
						quizAnswers: data.quiz_answers ?? data.quizAnswers ?? [],
						approvedQuiz: (data.approved_quiz ?? data.approvedQuiz) === true,
						completedAt: data.completed_at ?? data.completedAt ?? null,
					} as QuizAttemptHistory;
				})
				.sort((left, right) => left.attempt - right.attempt);
		} catch (error) {
			console.error('Failed to read quiz attempt history:', error);
			return [];
		}
	}

	listen(
		userId: string,
		categoryEnrollmentId: string,
		courseEnrollmentId: string,
		projectId: string,
		next: (courseEnrollment: CourseEnrollment | null) => void,
		error?: (error: unknown) => void
	): () => void {
		return this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollmentId)
			.collection(CourseEnrollment.PATH)
			.doc(courseEnrollmentId)
			.onSnapshot(
				snapshot => next(snapshot.exists
					? this.withLocalProgress(new CourseEnrollment(snapshot.data()), { userId, categoryEnrollmentId, courseEnrollmentId })
					: null),
				error
			);
	}

	// Every copy of the enrollment the app reads carries the progress this device made but has not
	// managed to save yet; otherwise a fresh read (a reload, a live snapshot) silently rolls it back.
	private withLocalProgress(courseEnrollment: CourseEnrollment, context: ProgressSyncContext): CourseEnrollment {
		if (this.progressSync.apply(context, courseEnrollment.chapterEnrollments)) {
			courseEnrollment.completedPercentage = computeCompletedPercentage(courseEnrollment.chapterEnrollments);
		}
		return courseEnrollment;
	}

	async get(userId: string, categoryEnrollmentId: string, courseEnrollmentId: string, projectId: string): Promise<CourseEnrollment | null> {
		const snapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollmentId)
			.collection(CourseEnrollment.PATH)
			.doc(courseEnrollmentId)
			.get();
		if (snapshot.exists) {
			return this.withLocalProgress(new CourseEnrollment(snapshot.data()), { userId, categoryEnrollmentId, courseEnrollmentId });
		}
		return null;
	}

	async getByCourse(userId: string, categoryEnrollmentId: string, courseId: string, projectId: string): Promise<CourseEnrollment | null> {
		const snapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollmentId)
			.collection(CourseEnrollment.PATH)
			.where('course_id', '==', courseId)
			.get();
		if (snapshot.docs[0].exists) {
			return new CourseEnrollment(snapshot.docs[0].data());
		}
		return null;
	}

	async save(courseEnrollmentModel: CourseEnrollment, userId: string, categoryEnrollmentId: string, courseEnrollmentId: string, projectId: string) {
		const json = courseEnrollmentModel.toJson();
		const chapterEnrollments = json?.chapter_enrollments;
		delete json.chapter_enrollments;
		const docRef = this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollmentId)
			.collection(CourseEnrollment.PATH)
			.doc(courseEnrollmentId);
		if (Array.isArray(chapterEnrollments) && chapterEnrollments.length) {
			await this.writeChapterEnrollments(docRef, chapterEnrollments, json, { userId, categoryEnrollmentId, courseEnrollmentId });
		} else {
			await docRef.set(json, { merge: true });
		}
	}

	private mergeChapterCompletionSignals(currentChapters: any[] = [], incomingChapters: any[] = []): any[] {
		const currentById = new Map<string, any>();
		for (const chapter of currentChapters) {
			if (chapter?.id != null) {
				currentById.set(chapter.id, chapter);
			}
		}

		return incomingChapters.map((incoming) => {
			const current = currentById.get(incoming?.id);
			if (!current) {
				return incoming;
			}

			const merged = { ...incoming };

			// The attempt counter is advanced server-side by the retake grant and only ever moves forward.
			// A client that read the enrollment before the grant landed would otherwise rewind it here and
			// hand the learner a question set they have already sat.
			const currentAttempt = Number(current?.quiz_attempt ?? current?.quizAttempt ?? 0);
			const incomingAttempt = Number(incoming?.quiz_attempt ?? incoming?.quizAttempt ?? 0);
			if (currentAttempt > incomingAttempt) {
				merged.quiz_attempt = currentAttempt;
				// The grant that advanced the counter also issued this flag; keeping the stale `false` would
				// consume a retake the learner never got to sit.
				merged.retake_granted = current?.retake_granted ?? current?.retakeGranted ?? false;
				// The rest of the incoming quiz state describes the OLDER attempt: letting it through
				// would stamp a previous attempt's score/answers onto the freshly granted one — the
				// server would read that as the new attempt being sat and failed (a component holding a
				// pre-grant copy of the enrollment, e.g. a save-on-navigate, is enough to hit this).
				// Everything attempt-scoped is therefore taken from the stored document instead.
				merged.quiz_score = current?.quiz_score ?? current?.quizScore ?? null;
				merged.quiz_answers = current?.quiz_answers ?? current?.quizAnswers ?? [];
				merged.approved_quiz = (current?.approved_quiz ?? current?.approvedQuiz) === true;
				merged.completed_at = current?.completed_at ?? current?.completedAt ?? null;
			}

			// Everything below rescues a field the incoming write looks like it's about to blank out, on
			// the assumption that "current" and "incoming" describe the same attempt and the incoming
			// write simply lost a race. In-person mode breaks that assumption: a grant can advance the
			// attempt server-side between this client's read and its write, so "current" can legitimately
			// belong to a LATER attempt than "incoming" — and rescuing across that boundary means
			// pasting one attempt's score/answers/completion onto a different attempt's record. Only
			// rescue when they still agree on which attempt is active.
			const sameAttempt = currentAttempt === incomingAttempt;

			const currentCompletedAt = current?.completed_at ?? current?.completedAt ?? null;
			const incomingCompletedAt = incoming?.completed_at ?? incoming?.completedAt ?? null;
			if (sameAttempt && currentCompletedAt && !incomingCompletedAt) {
				merged.completed_at = currentCompletedAt;
			}

			if ((current?.is_video_watched ?? current?.isVideoWatched) && !(incoming?.is_video_watched ?? incoming?.isVideoWatched)) {
				merged.is_video_watched = true;
			}

			if (sameAttempt && (current?.approved_quiz ?? current?.approvedQuiz) && !(incoming?.approved_quiz ?? incoming?.approvedQuiz)) {
				merged.approved_quiz = true;
			}

			const currentQuizAnswers = current?.quiz_answers ?? current?.quizAnswers ?? null;
			const incomingQuizAnswers = incoming?.quiz_answers ?? incoming?.quizAnswers ?? null;
			const incomingHasAnswers = Array.isArray(incomingQuizAnswers) && incomingQuizAnswers.length > 0;
			const currentHasAnswers = Array.isArray(currentQuizAnswers) && currentQuizAnswers.length > 0;
			if (sameAttempt && currentHasAnswers && !incomingHasAnswers) {
				merged.quiz_answers = currentQuizAnswers;
			}

			const currentQuizScore = current?.quiz_score ?? current?.quizScore ?? null;
			const incomingQuizScore = incoming?.quiz_score ?? incoming?.quizScore ?? null;
			if (sameAttempt && currentQuizScore != null && incomingQuizScore == null) {
				merged.quiz_score = currentQuizScore;
			}

			// The watched position only moves forward too: a copy read before the last save must not
			// send the learner back to an earlier point of the video.
			const currentPosition = Number(current?.video_current_time ?? current?.videoCurrentTime ?? 0) || 0;
			const incomingPosition = Number(incoming?.video_current_time ?? incoming?.videoCurrentTime ?? 0) || 0;
			if (currentPosition > incomingPosition) {
				merged.video_current_time = currentPosition;
			}

			// Unlocking is one-way as well. A stale copy used to write `locked_chapter: true` back over
			// a chapter that had already been opened up.
			if ((current?.locked_chapter ?? current?.lockedChapter) === false) {
				merged.locked_chapter = false;
			}

			return merged;
		}).map((chapter, index, chapters) => {
			// Finishing a chapter opens the next one, whichever write did the finishing.
			const previous = chapters[index - 1];
			if (chapter && (chapter.completed_at ?? chapter.completedAt ?? (previous && (previous.completed_at ?? previous.completedAt)))) {
				return { ...chapter, locked_chapter: false };
			}
			return chapter;
		});
	}

	// Runs writes to the same document one after another (see `writeQueues`).
	private enqueueWrite<T>(documentPath: string, write: () => Promise<T>): Promise<T> {
		const previous = this.writeQueues.get(documentPath) ?? Promise.resolve();
		let staleTimer: ReturnType<typeof setTimeout>;
		const previousSettledOrStale = Promise.race([
			previous.catch(() => undefined),
			new Promise(resolve => (staleTimer = setTimeout(resolve, WRITE_QUEUE_MAX_WAIT_MS))),
		]).finally(() => clearTimeout(staleTimer));
		const run = previousSettledOrStale.then(write);
		this.writeQueues.set(documentPath, run.catch(() => undefined));
		return run;
	}

	private writeChapterEnrollments(
		docRef: firebase.firestore.DocumentReference,
		chapterEnrollments: any[],
		extraFields: { [key: string]: any } = {},
		context?: ProgressSyncContext,
	): Promise<void> {
		return this.enqueueWrite(docRef.path, async () => {
			let persistedChapters: any[] = [];
			await this.runChapterEnrollmentsTransaction(docRef, chapterEnrollments, extraFields, merged => (persistedChapters = merged));
			// The transaction may land well after the caller stopped waiting for it (see withTimeout);
			// acknowledging here, not in the caller, is what clears the local copy in that case too.
			if (context) {
				this.progressSync.acknowledge(context, persistedChapters);
			}
		});
	}

	private async runChapterEnrollmentsTransaction(
		docRef: firebase.firestore.DocumentReference,
		chapterEnrollments: any[],
		extraFields: { [key: string]: any },
		onMerged: (mergedChapters: any[]) => void,
	): Promise<void> {
		await this.db.firestore.runTransaction(async (transaction) => {
			const snapshot = await transaction.get(docRef);
			const currentData = snapshot.exists ? snapshot.data() : {};
			const currentChapters = currentData?.['chapter_enrollments'] ?? [];
			const mergedChapters = this.mergeChapterCompletionSignals(currentChapters as any[], chapterEnrollments ?? []);
			onMerged(mergedChapters);
			// Counted over the chapters actually enrolled, like the server's recalculation. The stored
			// `total_chapters` drifts when a chapter is removed and then no course can reach 100%.
			const configuredTotal = Number(
				extraFields?.['total_chapters'] ??
				currentData?.['total_chapters'] ??
				mergedChapters.length
			);
			const totalChapters = mergedChapters.length || (Number.isFinite(configuredTotal) ? configuredTotal : 0);
			const completedChapters = mergedChapters.filter(chapter =>
				!!(chapter?.completed_at ?? chapter?.completedAt)
			).length;
			const completedPercentage = totalChapters
				? Math.min(100, Math.round((completedChapters / totalChapters) * 100))
				: 0;
			const currentLastChapterSeen = isNullOrUndefined(currentData?.['last_chapter_seen'])
				? Number.NaN
				: Number(currentData?.['last_chapter_seen']);
			const incomingLastChapterSeen = isNullOrUndefined(extraFields?.['last_chapter_seen'])
				? Number.NaN
				: Number(extraFields?.['last_chapter_seen']);
			const lastCompletedChapter = mergedChapters.reduce(
				(lastIndex, chapter, index) =>
					(chapter?.completed_at ?? chapter?.completedAt) ? index : lastIndex,
				-1
			);
			const lastChapterSeen = Math.max(
				Number.isFinite(currentLastChapterSeen) ? currentLastChapterSeen : -1,
				Number.isFinite(incomingLastChapterSeen) ? incomingLastChapterSeen : -1,
				lastCompletedChapter
			);
			const updateData: { [key: string]: any } = {
				...extraFields,
				chapter_enrollments: mergedChapters,
				completed_percentage: completedPercentage,
			};
			if (mergedChapters.length) {
				updateData['total_chapters'] = mergedChapters.length;
			}

			if (lastChapterSeen >= 0) {
				updateData['last_chapter_seen'] = lastChapterSeen;
			}
			if (completedPercentage === 100) {
				updateData['completed_at'] =
					currentData?.['completed_at'] ??
					extraFields?.['completed_at'] ??
					firebase.firestore.Timestamp.now();
			} else {
				updateData['completed_at'] = null;
			}

			transaction.set(docRef, updateData, { merge: true });
		});
	}

	/**
	 * Persists the learner's chapter progress.
	 *
	 * Resolves `true` once the write has landed, `false` when it could not be persisted. It never
	 * throws: callers that need to react to a lost write check the result, and the progress itself is
	 * never lost — it stays on the device (ProgressSyncService) and rides along with the next write.
	 */
	async saveChapterEnrollment(courseEnrollment: CourseEnrollment, userId: string, categoryEnrollmentId: string, projectId: string, chapterEnrollment?: ChapterEnrollment): Promise<boolean> {
		if (courseEnrollment?.completedAt) {courseEnrollment.completedPercentage = 100 }
		const json = courseEnrollment.toJson();
		const context = { userId, categoryEnrollmentId, courseEnrollmentId: courseEnrollment?.id };
		const docRef = this.db.getProyIdRef(projectId)
			.collection(User.PATH).doc(userId)
			.collection(CategoryEnrollment.PATH).doc(categoryEnrollmentId)
			.collection(CourseEnrollment.PATH).doc(courseEnrollment?.id);

		const lastError = await this.persistChapterEnrollments(docRef, courseEnrollment.chapterEnrollments, json?.chapter_enrollments ?? [], {
			completed_percentage: json?.completed_percentage,
			completed_at: json?.completed_at,
			last_chapter_seen: json?.last_chapter_seen,
		}, context, 'chapter progress write failed');
		if (!lastError) {
			return true;
		}

		this.diagnostics.error(lastError, 'Chapter progress write exhausted retries', {
			courseEnrollmentId: courseEnrollment?.id ?? null,
			chapterEnrollmentId: chapterEnrollment?.id ?? null,
			code: (lastError as any)?.code ?? null,
			signedIn: !!this.authService.auth?.user?.id,
		}, { event: 'progress-write-failed' });
		await this.logVideoError((categoryEnrollmentId || 'unknown'), (courseEnrollment?.id || 'unknown'), (chapterEnrollment?.id || 'unknown'), lastError, {
			course_enrollment: JSON.stringify(courseEnrollment),
			completed_at: JSON.stringify(courseEnrollment?.completedAt)
		}, 'chapterEnrollment3 error');
		return false;
	}

	async updateChapterEnrollments(courseEnrollment: CourseEnrollment, userId: string, categoryEnrollmentId: string, projectId: string): Promise<void> {
		try {
			if (!categoryEnrollmentId || !courseEnrollment?.id) {
				return;
			}
			const json = courseEnrollment.toJson();
			const context = { userId, categoryEnrollmentId, courseEnrollmentId: courseEnrollment.id };
			this.progressSync.record(context, courseEnrollment.chapterEnrollments);
			const docRef = this.db.getProyIdRef(projectId)
				.collection(User.PATH).doc(userId)
				.collection(CategoryEnrollment.PATH).doc(categoryEnrollmentId)
				.collection(CourseEnrollment.PATH).doc(courseEnrollment.id);
			await this.writeChapterEnrollments(docRef, json?.chapter_enrollments ?? [], {}, context);
		} catch (error) {
			await this.logUpdateError(error, courseEnrollment?.chapterEnrollments, 'updateChapterEnrollments', {
				userId,
				categoryEnrollmentId,
				courseEnrollmentId: courseEnrollment?.id,
			});
		}
	}

	/**
	 * Persists the learner's position in the current video.
	 *
	 * Runs on a timer and on component teardown — the moments a learner is most likely to be
	 * mid-navigation or on a weak mobile link — and carries every chapter completion accumulated in
	 * this session, not just the position.
	 */
	async saveVideoCurrentTime(courseEnrollmentModel: CourseEnrollment, userId: string, categoryEnrollmentId: string, projectId: string, updatedChapterEnrollments: ChapterEnrollment[]): Promise<boolean> {
		if (!userId || !categoryEnrollmentId || !courseEnrollmentModel?.id || (Array.isArray(updatedChapterEnrollments) && !updatedChapterEnrollments?.length)) {
			return false;
		}

		const json = courseEnrollmentModel.toJson();
		const context = { userId, categoryEnrollmentId, courseEnrollmentId: courseEnrollmentModel.id };
		const courseEnrollmentDocRef = this.db.getProyIdRef(projectId)
			.collection(User.PATH).doc(userId)
			.collection(CategoryEnrollment.PATH).doc(categoryEnrollmentId)
			.collection(CourseEnrollment.PATH).doc(courseEnrollmentModel.id);

		const lastError = await this.persistChapterEnrollments(courseEnrollmentDocRef, courseEnrollmentModel.chapterEnrollments,
			json?.chapter_enrollments ?? [], {}, context, 'video position write failed');
		if (!lastError) {
			return true;
		}

		await this.logUpdateError(lastError, updatedChapterEnrollments, 'saveVideoCurrentTime (empty array fix)', {
			userId,
			categoryEnrollmentId,
			courseEnrollmentId: courseEnrollmentModel?.id,
		});
		return false;
	}

	/**
	 * Records the progress on the device, then writes it. Resolves `null` when the write landed, or the
	 * last error.
	 *
	 * Each attempt is capped by a timeout because a Firestore transaction with no connectivity does not
	 * fail, it waits. A timed-out transaction is still running, though, so no new attempt is started
	 * behind it: a second transaction on the same document would only invalidate the first one's read
	 * and time out in turn (the old retry loop did exactly that). The one still in flight acknowledges
	 * the local copy itself if it lands later.
	 */
	private async persistChapterEnrollments(
		docRef: firebase.firestore.DocumentReference,
		modelChapters: ChapterEnrollment[],
		chapterEnrollmentsJson: any[],
		extraFields: { [key: string]: any },
		context: ProgressSyncContext,
		breadcrumb: string,
	): Promise<unknown | null> {
		this.progressSync.record(context, modelChapters);

		let lastError: unknown = null;
		for (let attempt = 0; attempt < SAVE_ATTEMPT_DELAYS_MS.length; attempt++) {
			try {
				await withTimeout(
					this.writeChapterEnrollments(docRef, chapterEnrollmentsJson, extraFields, context),
					SAVE_ATTEMPT_TIMEOUT_MS
				);
				return null;
			} catch (error) {
				lastError = error;
				const timedOut = String((error as any)?.message ?? '').startsWith(WRITE_TIMEOUT_MESSAGE);
				const delay = SAVE_ATTEMPT_DELAYS_MS[attempt];
				const retryable = !timedOut && isRetryableFirestoreError(error) && !isNullOrUndefined(delay);
				this.diagnostics.breadcrumb(breadcrumb, {
					attempt: attempt + 1,
					code: (error as any)?.code ?? null,
					message: (error as any)?.message ?? String(error),
					willRetry: retryable,
				});
				if (!retryable) {
					break;
				}
				await new Promise(resolve => setTimeout(resolve, delay));
			}
		}
		// The Firestore write failed; the progress endpoint is a single HTTPS request and often gets
		// through where the transaction's round trips did not.
		if (await this.progressSync.sendToServer(context)) {
			this.diagnostics.breadcrumb('progress saved through the server after the Firestore write failed', {
				code: (lastError as any)?.code ?? null,
			});
			return null;
		}
		this.progressSync.markFailed(context);
		return lastError ?? new Error('Chapter progress could not be persisted');
	}

	async getAll(userId: string, projectId: string): Promise<CourseEnrollment[]> {
		const courseEnrollments: CourseEnrollment[] = [];

		const categorySnapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.get();

		for (const categoryDoc of categorySnapshot.docs) {
			const categoryEnrollmentId = categoryDoc.id;

			const courseSnapshot = await this.db.firestore
				.collection(Project.PATH)
				.doc(projectId)
				.collection(User.PATH)
				.doc(userId)
				.collection(CategoryEnrollment.PATH)
				.doc(categoryEnrollmentId)
				.collection(CourseEnrollment.PATH)
				.get();

			for (const courseDoc of courseSnapshot.docs) {
				const courseEnrollment = new CourseEnrollment(courseDoc.data());
				courseEnrollment.__categoryId = categoryEnrollmentId; // Asignar el ID de la categoría
				courseEnrollments.push(courseEnrollment);
			}
		}

		return courseEnrollments;
	}

	async setAsFavorite(categoryEnrollmentId: string, courseEnrollmentId: string, userId: string, projectId: string, favorite: boolean) {
		try {
			await this.db.firestore
				.collection(Project.PATH)
				.doc(projectId)
				.collection(User.PATH)
				.doc(userId)
				.collection(CategoryEnrollment.PATH)
				.doc(categoryEnrollmentId)
				.collection(CourseEnrollment.PATH)
				.doc(courseEnrollmentId)
				.update({ 'is_favorite': favorite });
		} catch (error) {
			await this.logUpdateError(error, favorite, 'setAsFavorite')
		}
	}

	async saveNoteOnUser(model: Note, userId: string, projectId: string): Promise<DocumentReference> {
		if (isNullOrUndefined(model.id)) {
			model.id = this.db.generateFirebaseUniqueId();
		}
		const json = model.toJson();
		const noteRef = this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(Note.PATH)
			.doc(model.id);

		await noteRef.set(json, { merge: true });
		return noteRef;
	}

	async getNextCourse(
		userId: string,
		categoryEnrollmentId: string,
		projectId: string,
		currentCourseEnrollmentModel: CourseEnrollment,
		enforceCourseOrder: boolean = false
	): Promise<string | null> {
		const nextCourse = await this.getCourseByIndex(userId, categoryEnrollmentId, projectId, currentCourseEnrollmentModel.index + 1);
		if (nextCourse) {
			return nextCourse;
		}
		if (enforceCourseOrder) return null;
		return await this.getAnyIncompleteCourse(
			userId,
			categoryEnrollmentId,
			projectId,
			currentCourseEnrollmentModel.id
		);
	}

	async canAccessCourseInOrder(
		userId: string,
		categoryEnrollmentId: string,
		courseEnrollmentId: string,
		projectId: string
	): Promise<boolean> {
		const snapshot = await this.getCollectionReference(userId, categoryEnrollmentId, projectId)
			.orderBy('index')
			.get();
		const courses = snapshot.docs.map(doc => new CourseEnrollment(doc.data()));
		return canAccessCourseInOrder(courses, courseEnrollmentId);
	}

	private getCollectionReference(userId: string, categoryEnrollmentId: string, projectId: string) {
		return this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollmentId)
			.collection(CourseEnrollment.PATH);
	}

	private async getCourseByIndex(userId: string, categoryEnrollmentId: string, projectId: string, nextIndex: number): Promise<string | null> {
		const snapshot = await this.getCollectionReference(userId, categoryEnrollmentId, projectId)
			.orderBy('index')
			.where('index', '==', nextIndex)
			.where('completed_at', '==', null)
			.limit(1)
			.get();

		return snapshot.empty ? null : snapshot.docs[0].id;
	}

	private async getAnyIncompleteCourse(userId: string, categoryEnrollmentId: string, projectId: string, excludedCourseId: string): Promise<string | null> {
		const snapshot = await this.getCollectionReference(userId, categoryEnrollmentId, projectId)
			.where('id', '!=', excludedCourseId)
			.where('completed_at', '==', null)
			.limit(1)
			.get();

		return snapshot.empty ? null : snapshot.docs[0].id;
	}

	async logVideoError(categoryId: string, courseId: string, chapterId: string, error: any, additionalData: any, title: string = "Video end error") {
		try {
			await this.db.getProyIdRef(environment.projectId)
				.collection('logging')
				.doc().set({
					title,
					user_id: this.authService.auth?.user?.id ?? null,
					error: JSON.stringify(describeError(error)) ?? null,
					category: categoryId ?? null,
					course: courseId ?? null,
					chapter: chapterId ?? null,
					timestamp: new Date(),
					current_url: this.router?.url ?? null,
					user_agent: navigator.userAgent,
					additional_data: JSON.parse(JSON.stringify(additionalData ?? null))
				});
		} catch (loggingError) {
			console.error('logVideoError failed to write log entry:', loggingError);
		}
	}

	async logQuestionMissingError(categoryId: string, courseId: string, chapterId: string, currentChapterIndex: number, quiz: Quiz, title: string = "Question text is missing") {
		const allQuizQuestionsHaveText = (quiz?.questions || [])?.every(question => question?.text && isValidString(question?.text));
		if (allQuizQuestionsHaveText) {
			return;
		}
		if (currentChapterIndex <= ((quiz?.questions ?? []).length - 1)) {
			return;
		}
		try {
			await this.db.getProyIdRef(environment.projectId)
				.collection('logging')
				.doc().set({
					title,
					user_id: this.authService.auth?.user?.id ?? null,
					category: categoryId ?? null,
					course: courseId ?? null,
					chapter: chapterId ?? null,
					timestamp: new Date(),
					current_url: this.router?.url ?? null,
					user_agent: navigator.userAgent,
					currentChapterIndex: currentChapterIndex ?? 'CHAPTER IS NAN',
					quiz: JSON.stringify(quiz) ?? null
				});
		} catch (loggingError) {
			console.error('logQuestionMissingError failed to write log entry:', loggingError);
		}
	}

	/**
	 * Records a write that could not be persisted, together with the payload it was carrying.
	 *
	 * The entry is what makes the loss recoverable, so it stores the enrollment it belongs to and the
	 * id the write actually targeted. Without the path, a payload can only be traced back to a
	 * document by matching chapter ids; and `path_user_id` is what distinguishes a lost write from a
	 * write aimed at the wrong account, since `user_id` records who was signed in, not who was
	 * written to — when the two disagree, that disagreement is the failure.
	 */
	async logUpdateError(error: any, toSave: any, title: string, context: { userId?: string; categoryEnrollmentId?: string; courseEnrollmentId?: string } = {}) {
		try {
			const described = describeError(error);
			await this.db.getProyIdRef(environment.projectId)
				.collection('logging').doc().set({
					title,
					error: {
						message: described['message'] ?? 'error not an object',
						code: described['code'] ?? null,
						title: error?.title ?? null
					},
					userId: this.authService.auth?.user?.id ?? null,
					path_user_id: context.userId ?? null,
					category: context.categoryEnrollmentId ?? null,
					course: context.courseEnrollmentId ?? null,
					current_url: this.router?.url ?? null,
					timestamp: new Date(),
					content: JSON.stringify(toSave, (_key, value) => value === undefined ? null : value) ?? null
				});
		} catch (loggingError) {
			console.error('logUpdateError failed to write log entry:', loggingError);
		}
	}

}
